import { useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Card, Alert, Tabs, Tab,
  TextField, InputAdornment, Chip, IconButton, Tooltip, Button,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScienceIcon from '@mui/icons-material/Science';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import { useAuth } from '../../context/AuthContext';
import { useTransacciones } from '../../hooks/useTransacciones';
import { useCvuConfig } from '../../hooks/useCvus';
import { formatCurrency, formatDateTime } from '../../utils/format';
import {
  type TransaccionRecaudacion,
  type EstadoTransaccion,
  getEstadoTrxLabel,
  getEstadoTrxColor,
} from '../../types/transaccion';
import ConciliarDialog from './ConciliarDialog';
import SimuladorWebhookDialog from './SimuladorWebhookDialog';
import ConsultarPagosDialog from './ConsultarPagosDialog';

type TabValue = 'pre_confirmada' | 'sin_identificar' | 'acreditada' | 'todas';

const ESTADO_POR_TAB: Record<TabValue, EstadoTransaccion | undefined> = {
  pre_confirmada:  'pre_confirmada',
  sin_identificar: 'sin_identificar',
  acreditada:      'acreditada',
  todas:           undefined,
};

export default function TransaccionesList() {
  const { effectiveRole } = useAuth();
  const puedeOperar =
    effectiveRole === 'admin_general' || effectiveRole === 'operador';

  const { data: ppConfig } = useCvuConfig();
  // Simulador visible solo en dev (mode=mock o NODE_ENV no-prod)
  // El backend rechaza en producción aunque el botón esté visible.
  const mostrarSimulador = ppConfig?.mode === 'mock' || import.meta.env.DEV;

  const [tab, setTab] = useState<TabValue>('pre_confirmada');
  const [busqueda, setBusqueda] = useState('');
  const [accion, setAccion] = useState<{
    trx: TransaccionRecaudacion;
    modo: 'ver' | 'conciliar' | 'rechazar';
  } | null>(null);
  const [openSimulador, setOpenSimulador] = useState(false);
  const [openConsultar, setOpenConsultar] = useState(false);

  const filtros = useMemo(
    () => ({ estado: ESTADO_POR_TAB[tab] }),
    [tab],
  );

  const { data: trxs = [], isError, isLoading } = useTransacciones(filtros);

  const filtered = useMemo(() => {
    if (!busqueda.trim()) return trxs;
    const q = busqueda.toLowerCase();
    return trxs.filter((t) =>
      t.empresa?.razon_social?.toLowerCase().includes(q) ||
      t.empresa?.cuit?.toLowerCase().includes(q) ||
      t.cvu_empresa?.alias?.toLowerCase().includes(q) ||
      t.referencia_externa?.toLowerCase().includes(q),
    );
  }, [trxs, busqueda]);

  const countSinIdentificar = trxs.filter(
    (t) => t.estado === 'sin_identificar',
  ).length;

  // ── Columns ─────────────────────────────────────────────────────────────────

  const columns: GridColDef<TransaccionRecaudacion>[] = [
    {
      field: 'fecha_acreditacion',
      headerName: 'Fecha',
      width: 160,
      renderCell: (p: GridRenderCellParams<TransaccionRecaudacion>) =>
        formatDateTime(p.row.fecha_acreditacion),
    },
    {
      field: 'empresa',
      headerName: 'Empresa',
      flex: 1.3,
      minWidth: 180,
      renderCell: (p: GridRenderCellParams<TransaccionRecaudacion>) =>
        p.row.empresa?.razon_social ?? (
          <Typography variant="caption" color="text.secondary">
            sin identificar
          </Typography>
        ),
    },
    {
      field: 'cvu_alias',
      headerName: 'Alias',
      width: 160,
      renderCell: (p: GridRenderCellParams<TransaccionRecaudacion>) => (
        <Typography variant="body2" fontFamily="monospace" fontSize={12}>
          {p.row.cvu_empresa?.alias ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'monto',
      headerName: 'Monto',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p: GridRenderCellParams<TransaccionRecaudacion>) => (
        <Typography variant="body2" fontWeight={500}>
          {formatCurrency(p.row.monto)}
        </Typography>
      ),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 140,
      renderCell: (p: GridRenderCellParams<TransaccionRecaudacion>) => (
        <Chip
          label={getEstadoTrxLabel(p.row.estado)}
          color={getEstadoTrxColor(p.row.estado)}
          size="small"
        />
      ),
    },
    {
      field: 'referencia_externa',
      headerName: 'Ref. externa',
      width: 160,
      renderCell: (p: GridRenderCellParams<TransaccionRecaudacion>) => (
        <Typography variant="caption" fontFamily="monospace">
          {p.row.referencia_externa ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams<TransaccionRecaudacion>) => {
        const pendiente =
          p.row.estado === 'pre_confirmada' ||
          p.row.estado === 'sin_identificar';
        return (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Ver detalle">
              <IconButton
                size="small"
                onClick={() => setAccion({ trx: p.row, modo: 'ver' })}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {puedeOperar && pendiente && (
              <>
                <Tooltip title="Conciliar contra cupón">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => setAccion({ trx: p.row, modo: 'conciliar' })}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Rechazar">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setAccion({ trx: p.row, modo: 'rechazar' })}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight={600}>
          Transacciones de recaudación
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            color="info"
            startIcon={<CloudDownloadIcon />}
            onClick={() => setOpenConsultar(true)}
          >
            Consultar PP (hoy)
          </Button>
          {mostrarSimulador && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<ScienceIcon />}
              onClick={() => setOpenSimulador(true)}
            >
              Simular webhook
            </Button>
          )}
        </Stack>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as TabValue)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="pre_confirmada" label="Pre-confirmadas" />
        <Tab
          value="sin_identificar"
          label={
            countSinIdentificar > 0
              ? `Sin identificar (${countSinIdentificar})`
              : 'Sin identificar'
          }
        />
        <Tab value="acreditada" label="Acreditadas" />
        <Tab value="todas" label="Todas" />
      </Tabs>

      <TextField
        size="small"
        placeholder="Buscar por empresa, CUIT, alias, referencia…"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        sx={{ mb: 2, width: 380 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar transacciones.
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={filtered}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
          localeText={{
            noRowsLabel:
              tab === 'pre_confirmada'
                ? 'Sin transacciones pendientes de confirmar'
                : 'Sin transacciones en este tab',
          }}
        />
      </Card>

      {accion && (
        <ConciliarDialog
          open
          trx={accion.trx}
          modo={accion.modo}
          onClose={() => setAccion(null)}
        />
      )}

      {openSimulador && (
        <SimuladorWebhookDialog
          open
          onClose={() => setOpenSimulador(false)}
        />
      )}

      {openConsultar && (
        <ConsultarPagosDialog
          open
          onClose={() => setOpenConsultar(false)}
        />
      )}
    </Box>
  );
}
